import { defineStore } from 'pinia';
import { useSentry } from '@keap-web/analytics-lib/sentry';
import {
    loadAccounts, loadPartnerGroups, createPartnerGroup, addUserToPartnerGroup, loadUserPermissions,
} from '@/accounts/api';
import {
    LOADING, SUCCESS, ERROR, IDLE,
} from '@/constants/loadingStatuses.constants';
import { useShareStore } from '@/share/store';

export const useAccountsStore = defineStore('accounts', {
    state: () => ({
        appId: '',
        apps: [],
        allAppsMap: {},
        sandboxes: [],
        loadAccountsStatus: IDLE,
        partnerGroup: {},
        sentry: useSentry(),
        userPermissions: [],
    }),

    getters: {
        getCompanyName: ({ allAppsMap }) => (tenantId) => {
            return allAppsMap[tenantId]?.companyName || '';
        },

        hasPermission: ({ userPermissions }) => (permission) => {
            return userPermissions.includes(permission);
        },
    },

    actions: {
        updateAppId(appId) {
            this.appId = appId;
        },

        setApps(accounts) {
            this.apps = accounts;
        },

        setAllApps(accounts) {
            accounts.forEach((account) => {
                this.allAppsMap[account.tenantId] = account;
            });
        },

        setSandboxes(accounts) {
            this.sandboxes = accounts;
        },

        setPartnerGroup(group) {
            this.partnerGroup = group;
        },

        setUserPermissions(permissions) {
            this.userPermissions = permissions;
        },

        loadAccountsStart() {
            this.loadAccountsStatus = LOADING;
        },

        loadAccountsError() {
            this.loadAccountsStatus = ERROR;
        },

        loadAccountsSuccess() {
            this.loadAccountsStatus = SUCCESS;
        },

        async loadAccounts(params, { includeExportSource } = {}) {
            try {
                this.loadAccountsStart();
                const accounts = await loadAccounts(params);

                if (includeExportSource) {
                    const appId = useShareStore().exportSourceAppId || this.appId;
                    const account = await this.loadAccountIfNotInList(appId, accounts);

                    if (account) {
                        accounts.unshift({ tenantId: appId, companyName: account.companyName });
                    }
                }

                this.setApps(accounts);
                this.loadAccountsSuccess();
            } catch (e) {
                this.sentry.captureException(e, {
                    message: 'Load accounts failed',
                });

                this.loadAccountsError();
            }
        },

        async loadAccountIfNotInList(appId, accounts) {
            const existsInList = accounts.find(({ tenantId }) => tenantId === appId);

            if (existsInList) {
                return null;
            }

            let currentApp = this.apps.find(({ tenantId }) => tenantId === appId);

            if (!currentApp) {
                const appResponse = await loadAccounts({ pageSize: 1, searchText: appId });

                [currentApp] = appResponse;
            }

            return currentApp;
        },

        async loadAccountsForExportSource() {
            return this.loadAccounts(undefined, { includeExportSource: true });
        },

        async loadAllAccounts() {
            if (Object.keys(this.allAppsMap).length > 0) {
                return;
            }

            try {
                const accounts = await loadAccounts({ pageSize: 500 });

                this.setAllApps(accounts);
            } catch (e) {
                this.sentry.captureException(e, {
                    message: 'Load all accounts failed',
                });
            }
        },

        async loadSandboxes() {
            if (!this.sandboxes.length) {
                const { appId } = this;
                const sandboxes = await loadAccounts({ pageSize: 100, sandboxOnly: true });

                const account = await this.loadAccountIfNotInList(appId, sandboxes);

                if (account) {
                    sandboxes.unshift({ tenantId: appId, companyName: account.companyName });
                }

                this.setSandboxes(sandboxes);
            }
        },

        async loadPartnerGroups({ tenantId, userId }) {
            const { userGroups } = await loadPartnerGroups({ userId, tenantId });

            if (userGroups.length === 0) {
                const partnerGroup = await createPartnerGroup({ tenantId, title: 'Default group' });

                await addUserToPartnerGroup({ partnerGroupId: partnerGroup.id, userId });

                userGroups.push({ group: partnerGroup });
            }

            this.setPartnerGroup(userGroups[0].group);
        },

        async loadPermissions({ tenantId, userId }) {
            try {
                const permissions = await loadUserPermissions({ tenantId, userId });

                this.setUserPermissions(permissions);
            } catch (e) {
                this.sentry.captureException(e, {
                    message: `Load user permissions failed for userId: ${userId} and tenantId: ${tenantId}`,
                });
            }
        },
    },

    persist: {
        paths: ['appId'],
    },
});
